import * as React from 'react';
import {h, VNode} from 'snabbdom';
import type {JsxVNodeChildren} from 'snabbdom-jsx-lite';

import {StrictJsxProps, stringifyAttrsIfPojo} from 'common/util/jsx';
import {flattenAttrsOn} from 'common/util/flatten-attrs-on';
import {react2panel} from 'common/util/react2panel';

import {TooltipContent, TooltipProps} from './component';
import {TooltipJsxProps} from './types';

type PanelTooltipProps = TooltipProps & {reference?: React.RefObject<HTMLElement>};

function PanelTooltipContent(props: PanelTooltipProps) {
  const [host, setHost] = React.useState<HTMLElement | null>(null);
  const attachRoot = React.useCallback((element: HTMLSpanElement | null) => {
    const root = element?.getRootNode();
    setHost(root instanceof ShadowRoot && root.host instanceof HTMLElement ? root.host : null);
  }, []);
  return (
    <span ref={attachRoot} style={{display: `contents`}}>
      {host && <TooltipContent {...props} host={host} />}
    </span>
  );
}

const Inner = react2panel(`mp-tooltip`, PanelTooltipContent, {useShadowDom: true});
export type TooltipPanelProps = StrictJsxProps<TooltipJsxProps> & {children?: JsxVNodeChildren};

function normalizeChildren(children: JsxVNodeChildren): Array<VNode | string | number> {
  if (Array.isArray(children)) return children.flatMap(normalizeChildren);
  if (children === null || children === undefined || typeof children === `boolean`) return [];
  return [children];
}

export function TooltipEl(props?: TooltipPanelProps, children?: JsxVNodeChildren): VNode {
  const {children: nestedChildren, ...panelProps} = props ?? {};
  const flattened = flattenAttrsOn<TooltipJsxProps>({...panelProps, on: undefined});
  const node = Inner({...flattened, ...props?.params, onShowed: props?.on?.showed});
  // Project the complete child list through the slot so mixed text and elements retain their order.
  const slotChildren = children ?? nestedChildren;
  if (slotChildren !== undefined && slotChildren !== null) {
    node.children = h(`span`, {}, normalizeChildren(slotChildren)).children;
  }
  node.key = props?.key;
  node.data = {
    ...node.data,
    attrs: {
      ...stringifyAttrsIfPojo({...props?.attrs}),
      'data-tooltip': ``,
      role: `tooltip`,
      hidden: true,
      'aria-hidden': `true`,
    },
    key: props?.key,
    class: props?.class,
    style: props?.style,
  };
  return node;
}

TooltipEl._tagName = (Inner as typeof Inner & {panelTagName: string}).panelTagName;
